import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ProfilePhoto } from "@/components/AuthImage";import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Heart, Check, X, Clock } from "@phosphor-icons/react";

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "bg-brand-gold/20", fg: "text-brand-gold", label: "Pending", Icon: Clock },
    accepted: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Accepted", Icon: Check },
    declined: { bg: "bg-red-100", fg: "text-red-700", label: "Declined", Icon: X },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 ${s.bg} ${s.fg} text-[10px] uppercase tracking-widest px-2 py-1`}>
      <s.Icon size={12} weight="fill" /> {s.label}
    </span>
  );
}

function Row({ item, onAction, direction }) {
  const p = item.profile;
  if (!p) return null;
  return (
    <div className="flex items-center gap-5 py-5 border-b border-brand-line" data-testid={`interest-row-${item.id}`}>
      <Link to={`/profile/${p.id}`} className="w-20 h-24 flex-shrink-0 overflow-hidden">
        <ProfilePhoto profile={p} className="w-full h-full object-cover" />
      </Link>
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <Link to={`/profile/${p.id}`} className="font-serif text-2xl text-brand-ink hover:text-brand-terracotta transition-colors">
            {p.name}{p.age ? `, ${p.age}` : ""}
          </Link>
          <StatusBadge status={item.status} />
        </div>
        <div className="text-sm text-brand-muted mt-1">
          {[p.profession, p.city, p.community].filter(Boolean).join(" · ")}
        </div>
      </div>
      {direction === "received" && item.status === "pending" && (
        <div className="flex gap-2">
          <Button onClick={() => onAction(item.id, "accept")} data-testid={`accept-${item.id}`} className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white">Accept</Button>
          <Button onClick={() => onAction(item.id, "decline")} variant="outline" data-testid={`decline-${item.id}`} className="rounded-full border-brand-line">Decline</Button>
        </div>
      )}
    </div>
  );
}

export default function Interests() {
  const [sent, setSent] = useState([]);
  const [recv, setRecv] = useState([]);

  const load = async () => {
    const [s, r] = await Promise.all([api.get("/interests/sent"), api.get("/interests/received")]);
    setSent(s.data.interests || []);
    setRecv(r.data.interests || []);
  };
  useEffect(() => { load(); }, []);

  const respond = async (interest_id, action) => {
    try {
      await api.post("/interests/respond", { interest_id, action });
      toast.success(action === "accept" ? "Interest accepted" : "Interest declined");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10" data-testid="interests-page">
      <div className="label-eyebrow text-brand-terracotta">Connections</div>
      <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mt-2 text-brand-ink">Interests</h1>

      <Tabs defaultValue="received" className="mt-10">
        <TabsList className="bg-transparent border-b border-brand-line rounded-none p-0 w-full justify-start gap-6">
          <TabsTrigger value="received" data-testid="tab-received" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-terracotta data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 label-eyebrow">Received ({recv.length})</TabsTrigger>
          <TabsTrigger value="sent" data-testid="tab-sent" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-terracotta data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 label-eyebrow">Sent ({sent.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="received" className="mt-6">
          {recv.length === 0 ? (
            <div className="border border-dashed border-brand-line p-16 text-center text-brand-muted" data-testid="received-empty">No interests received yet.</div>
          ) : recv.map((i) => <Row key={i.id} item={i} direction="received" onAction={respond} />)}
        </TabsContent>

        <TabsContent value="sent" className="mt-6">
          {sent.length === 0 ? (
            <div className="border border-dashed border-brand-line p-16 text-center text-brand-muted" data-testid="sent-empty">No interests sent yet.</div>
          ) : sent.map((i) => <Row key={i.id} item={i} direction="sent" />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
